import React, { createContext, useMemo } from 'react';
import { VictoryThemeDefinition, VictoryTheme } from 'victory';
import { mergeDeepRight } from 'ramda';
import { useTheme } from '@chakra-ui/react';
import { CHART_FONT_FAMILY as fontFamily } from './constants';

export const ChartThemeContext = createContext(VictoryTheme.grayscale);

export const getThemeColors = (themeColors: {
  [key: string]: Record<string, string> | string;
}) =>
  Object.keys(themeColors)
    .filter((colors) => !colors.includes('Alpha'))
    .reduce<string[]>((acc, colorKey) => {
      const color = themeColors[colorKey];
      if (typeof color === 'object') {
        return [...acc, color[500]];
      }
      return acc;
    }, []);

const ChartThemeProvider: React.FC = ({ children }) => {
  const chakraTheme = useTheme();
  const themeColors = chakraTheme.colors as {
    [key: string]: Record<string, string> | string;
  };

  const defaultColorScale = useMemo(() => getThemeColors(themeColors), [
    themeColors,
  ]);

  const theme: VictoryThemeDefinition = {
    line: {
      colorScale: defaultColorScale,
      style: {
        labels: {
          fontFamily,
        },
      },
    },
    scatter: {
      colorScale: defaultColorScale,
      style: {
        labels: {
          fontFamily,
        },
      },
    },
    axis: {
      style: {
        axisLabel: {
          fontFamily,
        },
        grid: {
          stroke: chakraTheme.colors.gray[300],
        },
        tickLabels: {
          fontFamily,
          fontSize: 14,
        },
      },
    },
    boxplot: {
      style: {
        min: {
          stroke: chakraTheme.colors.gray[500],
          strokeWidth: 2,
        },
        minLabels: {
          fontFamily,
        },
        max: {
          stroke: chakraTheme.colors.gray[500],
          strokeWidth: 2,
        },
        maxLabels: {
          fontFamily,
        },
        q1: {
          stroke: chakraTheme.colors.gray[500],
          fill: chakraTheme.colors.gray[500],
          fillOpacity: 0.2,
          strokeWidth: 2,
        },
        q1Labels: {
          fontFamily,
        },
        q3: {
          stroke: chakraTheme.colors.gray[500],
          fill: chakraTheme.colors.gray[500],
          fillOpacity: 0.2,
          strokeWidth: 2,
        },
        q3Labels: {
          fontFamily,
        },
        median: {
          stroke: chakraTheme.colors.gray[500],
        },
        medianLabels: {
          fontFamily,
        },
      },
    },
    stack: {
      colorScale: defaultColorScale,
    },
    legend: {
      colorScale: defaultColorScale,
      orientation: 'vertical',
      gutter: 20,
      y: 10,
      style: {
        data: {
          type: 'square',
        },
        labels: {
          fontFamily,
        },
        title: {
          fontFamily,
        },
      },
    },
    tooltip: {
      cornerRadius: 0,
      flyoutPadding: 10,
      flyoutStyle: {
        stroke: chakraTheme.colors.gray[400],
        fill: chakraTheme.colors.white,
      },
      style: {
        fontFamily,
      },
    },
    group: {
      colorScale: defaultColorScale,
    },
  };

  const compiledTheme = mergeDeepRight(
    VictoryTheme.grayscale,
    theme,
  ) as VictoryThemeDefinition;

  return (
    <ChartThemeContext.Provider value={compiledTheme}>
      {children}
    </ChartThemeContext.Provider>
  );
};

export default ChartThemeProvider;
